/**
 * Cierre de sesión por inactividad (Vue 2).
 * VUE_APP_IDLE_MINUTES: minutos sin actividad global antes del callback (default 15).
 *
 * Varias pestañas: se guarda la última actividad en localStorage; cualquier pestaña
 * que interactúe actualiza ese timestamp. Las demás escuchan `storage` y reinician
 * el temporizador. Al vencer el tiempo, se comprueba de nuevo el timestamp para no
 * cerrar sesión si otra pestaña estuvo activa recientemente.
 */

const STORAGE_KEY = 'rh_session_last_activity';

const EVENTS = [
  'mousemove',
  'mousedown',
  'keydown',
  'scroll',
  'touchstart',
  'click',
  'wheel',
];

let timerId = null;
let debounceId = null;
let ctx = null;
let idleCallbackRef = null;

function clearTimer() {
  if (timerId) {
    clearTimeout(timerId);
    timerId = null;
  }
}

function scheduleTimer(timeoutMs, onIdle) {
  clearTimer();
  timerId = setTimeout(onIdle, timeoutMs);
}

function bumpActivityTimestamp() {
  try {
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
  } catch (e) {
    /* private mode / quota */
  }
}

export function stop() {
  clearTimer();
  if (debounceId) {
    clearTimeout(debounceId);
    debounceId = null;
  }
  if (ctx) {
    EVENTS.forEach((ev) => window.removeEventListener(ev, ctx.onActivity));
    document.removeEventListener('visibilitychange', ctx.onVisibility);
    window.removeEventListener('storage', ctx.onStorage);
    ctx = null;
  }
  idleCallbackRef = null;
}

/**
 * @param {object} opts
 * @param {number} opts.timeoutMs - ms sin actividad en ninguna pestaña del mismo origen
 * @param {function} opts.onIdle
 */
export function start({ timeoutMs, onIdle }) {
  stop();
  const ms = Math.max(
    typeof timeoutMs === 'number' ? timeoutMs : 0,
    60 * 1000
  );

  const tryIdle = () => {
    const last = parseInt(localStorage.getItem(STORAGE_KEY), 10);
    const now = Date.now();
    if (Number.isFinite(last) && now - last < ms) {
      scheduleTimer(ms, idleCallbackRef);
      return;
    }
    stop();
    if (typeof onIdle === 'function') onIdle();
  };

  idleCallbackRef = tryIdle;

  const reset = () => {
    if (debounceId) clearTimeout(debounceId);
    debounceId = setTimeout(() => {
      debounceId = null;
      bumpActivityTimestamp();
      scheduleTimer(ms, idleCallbackRef);
    }, 800);
  };

  const onStorage = (e) => {
    if (e.key !== STORAGE_KEY || e.newValue == null) return;
    reset();
  };

  const onVisibility = () => {
    if (document.visibilityState === 'visible') {
      bumpActivityTimestamp();
      scheduleTimer(ms, idleCallbackRef);
    } else {
      clearTimer();
    }
  };

  const onActivity = () => {
    if (document.visibilityState === 'hidden') return;
    reset();
  };

  ctx = { onActivity, onVisibility, onStorage };

  window.addEventListener('storage', onStorage);
  EVENTS.forEach((ev) =>
    window.addEventListener(ev, onActivity, { passive: true })
  );
  document.addEventListener('visibilitychange', onVisibility);

  bumpActivityTimestamp();
  scheduleTimer(ms, idleCallbackRef);
}

export function getIdleMinutesFromEnv() {
  const n = Number(process.env.VUE_APP_IDLE_MINUTES);
  return Number.isFinite(n) && n > 0 ? n : 15;
}

/** Llamar al cerrar sesión (manual o idle) para no arrastrar actividad entre usuarios. */
export function clearIdleActivityMarker() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    /* ignore */
  }
}
